import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useToast } from "@/hooks/useToast";
import { notificationApi } from "@/lib/humhub-api/notification";

export interface PeriodicNotificationPayload {
  interval: number;
  limitSpacesMode: number;
  limitSpaces: string[];
  activities: string[];
}

export const usePeriodicNotificationSettings = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation();
  const [submitError, setSubmitError] = useState<string | null>(null);

  const {
    data: periodicNotificationSettings,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ["periodicNotificationSettings"],
    queryFn: () => notificationApi.getPeriodicNotificationSettings(),
    staleTime: 5 * 60 * 1000,
  });

  const { mutateAsync: updateSettings, isPending: isSaving } = useMutation({
    mutationFn: (payload: PeriodicNotificationPayload) =>
      notificationApi.updatePeriodicNotificationSettings({
        interval: payload.interval,
        limit_spaces_mode: payload.limitSpacesMode,
        limit_spaces: payload.limitSpaces,
        activities: payload.activities,
      }),
    onSuccess: (data) => {
      queryClient.setQueryData(["periodicNotificationSettings"], data);
      setSubmitError(null);

      toast({
        title: t("common.settingsSaved"),
        description: t("common.periodicNotificationSettingsSaved"),
      });
    },
    onError: (error) => {
      const errorMessage = error instanceof Error ? error.message : "Failed to update settings";
      setSubmitError(errorMessage);

      toast({
        title: t("common.settingsError"),
        description: errorMessage,
      });
    },
  });

  const saveSettings = async (values: PeriodicNotificationPayload) => {
    await updateSettings(values);
  };

  return {
    periodicNotificationSettings,
    isLoading,
    isSaving,
    saveSettings,
    submitError,
    refetch,
  };
};
